import { Outlet, Link } from 'react-router-dom';
import { LogOut } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/stores/authStore';
import { useAppStore } from '@/stores/appStore';
import { stripPhonePrefix } from '@/utils/format';
import { cn } from '@/utils/cn';
import { AdminSidebar } from './AdminSidebar';

export function AdminLayout() {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const collapsed = useAppStore((s) => s.sidebarCollapsed);

  const roleLabelMap: Record<string, string> = {
    user: t('admin.roles.user'),
    admin: t('admin.roles.admin'),
    super_admin: t('admin.roles.super_admin'),
  };

  return (
    <div className="min-h-screen bg-background flex">
      <AdminSidebar />

      <div
        className={cn(
          'flex-1 flex flex-col min-w-0 transition-all duration-200 pt-14',
          collapsed ? 'md:ml-16' : 'md:ml-36'
        )}
      >
        <header className="h-14 bg-background border-b border-border flex items-center px-4 shrink-0 fixed top-0 left-0 right-0 z-50">
          <div className="flex items-center gap-3 min-w-fit">
            <Link to="/home" className="flex items-center gap-2.5">
              <img
                src="/logo-wizmage-32.png"
                alt="WizMage"
                className="w-8 h-8 rounded-lg shrink-0"
              />
              <span className="text-base font-bold text-text-primary hidden sm:inline whitespace-nowrap">
                {t('admin.title')}
              </span>
            </Link>
          </div>

          <div className="flex items-center gap-3 ml-auto">
            <span className="text-sm text-text-secondary hidden sm:inline">
              {user?.nickname || stripPhonePrefix(user?.phone) || ''}
            </span>

            {user?.role && (
              <span
                className={cn(
                  'px-2 py-0.5 rounded-full text-xs font-medium',
                  user.role === 'super_admin'
                    ? 'bg-warning/20 text-warning'
                    : 'bg-primary/20 text-primary-light'
                )}
              >
                {roleLabelMap[user.role] || user.role}
              </span>
            )}

            <button
              onClick={logout}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-text-secondary hover:text-error hover:bg-error/10 transition-colors"
            >
              <LogOut size={16} />
              <span className="hidden sm:inline">{t('admin.logout')}</span>
            </button>
          </div>
        </header>

        <main className="flex-1">
          <div className="p-4 md:p-6 max-w-[1600px] mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
